/* eslint-disable camelcase*/
import { Grid } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { usersService } from "../../../api";
import { SalutationType } from "../../../models/enums/salutationType.enum";
import { UserType } from "../../../models/enums/userType.enum";
import { UserData } from "../../../models/userData";
import CustomTextField from "../../forms/CustomTextField";
import { NIL as NIL_UUID } from "uuid";

interface SearchUserFieldProps {
  // eslint-disable-next-line no-unused-vars
  onChangeUserId: (id: string) => void;
  // eslint-disable-next-line react/require-default-props
  initialUser?: UserData;
}

/**
 * SearchUserField component that allows selecting a user by searching with debounce.
 *
 * @component
 * @param {SearchUserFieldProps} props - Props for the component
 * @param {UserData} [props.initialUser] - Initial user value to prefill the field
 * @param {(id: string) => void} props.onChangeUserId - Callback triggered when a user is selected
 */
export const SearchUserField: React.FC<SearchUserFieldProps> = (props) => {
  const { initialUser, onChangeUserId } = props;
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchOptions, setSearchOptions] = useState<UserData[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>();
  const [autocompleteValue, setAutocompleteValue] = useState<UserData>({
    academic_title: "",
    id: NIL_UUID,
    first_name: "",
    last_name: "",
    birthday: "",
    bsnr: "",
    efn: "",
    email: "",
    fullname: "",
    participation_types: [],
    username: "",
    lanr: "",
    mobile: "",
    phone: "",
    salutation: SalutationType.Neutral,
    user_type: UserType.Undefined,
    address: {
      city: "",
      street: "",
      zip_code: "",
      country: "",
    },
    terms_agreed_at: "",
    is_2fa_enabled: false,
    can_manage_organizations: true,
    temp_password: false,
  });
  const [editedSearch, setEditedSearch] = useState<boolean>(false);

  /**
   * Handles input changes to the search field and sets the query state.
   *
   * @param {React.ChangeEvent<HTMLTextAreaElement>} event - The change event
   */
  const handleSearchQueryChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedSearch(true);
    setSearchQuery(event.target.value);
  };

  /**
   * Triggers user search with a debounce effect when the query changes.
   */
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const query = searchQuery;

      if (query !== null && query !== undefined) {
        (async () => {
          if (query === searchQuery && query.length > 0) {
            const results = await usersService.getStatistics("?search=" + query);
            setSearchOptions(results.map((x) => x.user));
          }
        })();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, setSearchOptions]);

  /**
   * Clears search options when the dropdown closes.
   */
  useEffect(() => {
    if (!searchOpen) {
      setSearchOptions([]);
    }
  }, [searchOpen]);

  /**
   * Sets the initial value if no edits were made and an initial user is given.
   */
  useEffect(() => {
    if (!editedSearch && initialUser) {
      setAutocompleteValue(initialUser);
    }
  }, [editedSearch, setAutocompleteValue, initialUser]);

  return (
    <Autocomplete
      freeSolo
      id="user"
      noOptionsText="Kein Nutzer gefunden"
      // react shows loadingText if no options provided
      loadingText="Keine Nutzer gefunden"
      value={autocompleteValue}
      open={searchOpen}
      onOpen={() => {
        setSearchOpen(true);
      }}
      onClose={() => {
        setSearchOpen(false);
      }}
      // backend will filter the results
      // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
      filterOptions={(options, _) => {
        return options;
      }}
      onChange={(event, item) => {
        if (typeof item !== "string") {
          if (item) {
            onChangeUserId(item?.id);
          }
        }
      }}
      options={searchOptions}
      getOptionSelected={(option, value) => option.id === value.id}
      getOptionLabel={(option) => {
        return typeof option !== "string" && option.id !== NIL_UUID
          ? `${
              option.academic_title === null ? "" : option.academic_title + " "
            }${option.fullname}`
          : option.toString();
      }}
      renderOption={(option) => (
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs={12}>
            {`${
              option.academic_title === null ? "" : option.academic_title + " "
            }${option.fullname}${option.username ? " (" + option.username + ")" : ""}`}
            <br />
            {option.lanr ? "LANR: " + option.lanr : null}
          </Grid>
        </Grid>
      )}
      renderInput={(params) => (
        <CustomTextField
          {...params}
          label="Nutzer"
          name="user"
          value={searchQuery}
          onChange={handleSearchQueryChange}
        />
      )}
    />
  );
};
